import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconPhoto, IconTextSize, IconWritingSign, IconX } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import { PositionGlyph, videoTileClass, videoTrayClass } from '../../components/video/VideoOptionTiles';
import {
    MAX_WATERMARK_IMAGE_BYTES,
    MAX_WATERMARK_VIDEO_BYTES,
    POSITION_OPTIONS,
    addVideoWatermark,
    formatFileSize,
    isSupportedVideoFile,
    isSupportedWatermarkImageFile,
    watermarkedVideoName,
} from './watermarkVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const POSITION_LABEL_KEYS = {
    center: 'watermarkVideo.positions.center',
    'top-left': 'watermarkVideo.positions.topLeft',
    'top-right': 'watermarkVideo.positions.topRight',
    'bottom-left': 'watermarkVideo.positions.bottomLeft',
    'bottom-right': 'watermarkVideo.positions.bottomRight',
};

const WATERMARK_TYPES = [
    { id: 'text', labelKey: 'watermarkVideo.modeText', Icon: IconTextSize },
    { id: 'image', labelKey: 'watermarkVideo.modeImage', Icon: IconPhoto },
];

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-2.5 text-sm text-[#3D3833] outline-none placeholder:text-[#A8A099] focus:border-violet-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

export default function WatermarkVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const watermarkImageInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [watermarkType, setWatermarkType] = useState('text');
    const [watermarkText, setWatermarkText] = useState('© Sohozkaj');
    const [watermarkImage, setWatermarkImage] = useState(null);
    const [position, setPosition] = useState('bottom-right');
    const [opacity, setOpacity] = useState(50);
    const [imageScalePercent, setImageScalePercent] = useState(20);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const watermarkImagePreviewUrl = useMemo(
        () => (watermarkImage ? URL.createObjectURL(watermarkImage) : ''),
        [watermarkImage],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (watermarkImagePreviewUrl) URL.revokeObjectURL(watermarkImagePreviewUrl);
    }, [watermarkImagePreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const canApply = !!file && (
        (watermarkType === 'text' && watermarkText.trim())
        || (watermarkType === 'image' && watermarkImage)
    );

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('watermarkVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_WATERMARK_VIDEO_BYTES) {
            const msg = t('watermarkVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setWatermarkType('text');
        setWatermarkText('© Sohozkaj');
        setWatermarkImage(null);
        setPosition('bottom-right');
        setOpacity(50);
        setImageScalePercent(20);
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const handleWatermarkImageChange = (e) => {
        const selected = e.target.files?.[0];
        e.target.value = '';
        if (!selected) return;

        if (!isSupportedWatermarkImageFile(selected)) {
            const msg = t('watermarkVideo.errors.invalidWatermarkImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        if (selected.size > MAX_WATERMARK_IMAGE_BYTES) {
            const msg = t('watermarkVideo.errors.watermarkImageTooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setWatermarkImage(selected);
        invalidateResult();
        setError(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'NEED_TEXT') return t('watermarkVideo.errors.emptyText');
        if (code === 'NEED_IMAGE') return t('watermarkVideo.errors.noImage');
        if (code === 'OUT_OF_MEMORY') return t('watermarkVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'WATERMARK_FAILED') {
            return t('watermarkVideo.errors.failed');
        }
        return t('watermarkVideo.errors.failed');
    };

    const handleApplyWatermark = async () => {
        if (!canApply || processing) return;

        if (watermarkType === 'text' && !watermarkText.trim()) {
            const msg = t('watermarkVideo.errors.emptyText');
            notifications.show({ title: t('common.error'), message: msg, color: 'yellow' });
            return;
        }
        if (watermarkType === 'image' && !watermarkImage) {
            const msg = t('watermarkVideo.errors.noImage');
            notifications.show({ title: t('common.error'), message: msg, color: 'yellow' });
            return;
        }

        recordToolUsage('video/watermark', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await addVideoWatermark(file, {
                watermarkType,
                text: watermarkText,
                imageFile: watermarkImage,
                position,
                opacity,
                imageScalePercent,
            }, (value) => setProgress(value));

            setResult({
                name: watermarkedVideoName(file.name),
                originalSize: file.size,
                outputSize: blob.size,
                blob,
            });
            notifications.show({
                title: t('common.success'),
                message: t('watermarkVideo.done'),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: canApply,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`watermarkVideo.howTo.step${i}`)),
        [t],
    );

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('watermarkVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/watermark"
            subtitleKey="watermarkVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('watermarkVideo.apply'),
                onClick: handleApplyWatermark,
                disabled: !canApply || processing || !!result,
                loading: processing,
                icon: <IconWritingSign size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />
                    <input
                        ref={watermarkImageInputRef}
                        type="file"
                        accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp"
                        className="hidden"
                        onChange={handleWatermarkImageChange}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('watermarkVideo.dropTitle')}
                            hint={t('watermarkVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('watermarkVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <div className="space-y-3">
                    <ToolCard title={t('watermarkVideo.typeTitle')}>
                        <div className="space-y-3.5">
                            <div className={videoTrayClass('grid-cols-2')}>
                                {WATERMARK_TYPES.map(({ id, labelKey, Icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => {
                                            setWatermarkType(id);
                                            invalidateResult();
                                        }}
                                        title={t(labelKey)}
                                        aria-label={t(labelKey)}
                                        aria-pressed={watermarkType === id}
                                        className={videoTileClass(watermarkType === id)}
                                    >
                                        <Icon size={20} stroke={1.7} />
                                    </button>
                                ))}
                            </div>

                            {watermarkType === 'text' ? (
                                <label className="block space-y-1.5">
                                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('watermarkVideo.textLabel')}
                                    </span>
                                    <input
                                        type="text"
                                        value={watermarkText}
                                        onChange={(e) => {
                                            setWatermarkText(e.target.value);
                                            invalidateResult();
                                        }}
                                        placeholder={t('watermarkVideo.textPlaceholder')}
                                        className={inputClass}
                                    />
                                </label>
                            ) : (
                                <div className="space-y-3">
                                    {!watermarkImage ? (
                                        <button
                                            type="button"
                                            onClick={() => watermarkImageInputRef.current?.click()}
                                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-violet-300 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700 transition-colors hover:border-violet-400 dark:border-violet-700 dark:bg-violet-900/20 dark:text-violet-300"
                                        >
                                            <IconPhoto size={18} stroke={1.8} />
                                            {t('watermarkVideo.imageUpload')}
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2.5 rounded-xl border border-[#ECE8E4] bg-white px-2.5 py-2 dark:border-zinc-700 dark:bg-zinc-900">
                                            <img
                                                src={watermarkImagePreviewUrl}
                                                alt=""
                                                className="h-10 w-14 rounded-md object-contain bg-[#F7F5F3] dark:bg-zinc-800"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                                    {watermarkImage.name}
                                                </div>
                                                <div className="text-[11px] text-[#A8A099] dark:text-zinc-500">
                                                    {formatFileSize(watermarkImage.size)}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setWatermarkImage(null);
                                                    invalidateResult();
                                                }}
                                                aria-label={t('watermarkVideo.removeImage')}
                                                className="flex h-7 w-7 items-center justify-center rounded-md bg-[#F7F5F3] text-[#6B635C] hover:text-[#D33A2C] dark:bg-zinc-800 dark:text-zinc-400"
                                            >
                                                <IconX size={14} stroke={2} />
                                            </button>
                                        </div>
                                    )}
                                    <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                        {t('watermarkVideo.imageHint')}
                                    </p>
                                    <label htmlFor="watermark-video-scale" className="block space-y-1.5">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('watermarkVideo.scaleLabel', { value: toBnDigits(imageScalePercent) })}
                                        </span>
                                        <input
                                            id="watermark-video-scale"
                                            type="range"
                                            min={5}
                                            max={50}
                                            step={1}
                                            value={imageScalePercent}
                                            onChange={(e) => {
                                                setImageScalePercent(Number(e.target.value));
                                                invalidateResult();
                                            }}
                                            className="w-full accent-violet-500"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </ToolCard>

                    <ToolCard title={t('watermarkVideo.styleTitle')}>
                        <div className="space-y-3.5">
                            <div>
                                <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('watermarkVideo.positionTitle')}
                                </p>
                                {/* Glyphs only — each draws where the mark lands; the name
                                    is in the tooltip / aria-label. */}
                                <div className={videoTrayClass('grid-cols-5')}>
                                    {POSITION_OPTIONS.map((pos) => (
                                        <button
                                            key={pos}
                                            type="button"
                                            onClick={() => {
                                                setPosition(pos);
                                                invalidateResult();
                                            }}
                                            title={t(POSITION_LABEL_KEYS[pos])}
                                            aria-label={t(POSITION_LABEL_KEYS[pos])}
                                            aria-pressed={position === pos}
                                            className={videoTileClass(position === pos)}
                                        >
                                            <PositionGlyph position={pos} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <label htmlFor="watermark-video-opacity" className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('watermarkVideo.opacityLabel', { value: toBnDigits(opacity) })}
                                </span>
                                <input
                                    id="watermark-video-opacity"
                                    type="range"
                                    min={5}
                                    max={100}
                                    step={1}
                                    value={opacity}
                                    onChange={(e) => {
                                        setOpacity(Number(e.target.value));
                                        invalidateResult();
                                    }}
                                    className="w-full accent-violet-500"
                                />
                            </label>
                        </div>
                    </ToolCard>
                </div>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('watermarkVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('watermarkVideo.previewEmpty')}
                    idleHint={t('watermarkVideo.previewEmptyHint')}
                    processingLabel={t('watermarkVideo.applying')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('watermarkVideo.done')}
                    downloadLabel={t('watermarkVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('watermarkVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('watermarkVideo.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : t(POSITION_LABEL_KEYS[position])}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
